import * as React from 'react';
import * as ReactDOM from 'react-dom'
import 'react-toastify/dist/ReactToastify.css';
import { SystemButton } from '../tools/SystemButton';
import SystemDialog from '../tools/SystemDialog';
import { CrudHelper, EditModes, SystemPanel } from '..';
import LoadingPanel from '../common/SystemLoadingPanel';
import { IPermission, IUserDetail } from '../../library/types/ISecurity'
import { getPermission } from '../../service/userService'
import AuditForm from '../form/audit/AuditForm'
import SystemFormHelper, { ISystemFormHelper } from '../inputs/SystemFormHelper'
import { SuperAdminInfo } from '../common/ShowAdminInfo';

const showConsoleInfo = false;

export enum IPlacement {
    TopLeft, TopCenter, TopRight,
    BottomLeft, BottomCenter, BottomRight,
    MiddleRight, MiddleCenter, MiddleLeft
}

export interface ISystemFormProps {
    id?: string;
    name?: string;
    label?: string; // title of the form

    primaryKeyEncrypted?: string;
    secondaryKeyEncrypted?: string;

    children?: any; // child components of the form

    securityObject?: string; // contains authentication information of the user and the actions the user can perform 

    // crud permissions
    canSave?: boolean;
    canCreate?: boolean; // allows the form to create
    canRead?: boolean; // allows the form to read 
    canUpdate?: boolean; // allows the form to update
    canDelete?: boolean; // allows to form to delete
    disableSave?: boolean;
    // buttons to show
    canClear?: boolean; // allows to form to delete
    canReview?: boolean; // allows to form to delete
    showValidate?: boolean; // allows a form to validate
    showSearch?: boolean;
    showClear?: boolean;
    showAdd?: boolean;
    showSaveAndClose?: boolean;
    showSave?: boolean;
    showReview?: boolean;
    showTitle?: boolean;
    showTopOrBottomToolTip?: boolean
    ToolTipPosition?: string
    /** event raised on Add Button click (use this event to clear up the form and setup initial values) */
    onAdd?(): void;
    onSave?(): void; // event raised on save
    onSaveAndClose?(): void; // event raised on save
    onEdit?(): void;
    onCancel?(): void; // event raised on save
    onDelete?(): boolean;

    onAfterDelete?(response?: any): void;
    onAfterUpdate?(response?: any): void;
    onAfterCreate?(response?: any): void;
    onAfterRead?(response?: any): void;
    /** event raised on search */
    onSearch?(): void;

    /** event raised on clear click */
    onClear?(): void;
    onHideTile?(): void;
    onReview?(): void; // 
    onRead?(): void;
    onSaveAndClose?(): void;

    /** Validates a form - return true if validation is successfull */
    onValidate?(): boolean; // event raised on validate
    onValidateAsync?(): Promise<boolean>; // event raised on validate

    /** fires after save success */
    onAfterUpdate?(response: any): void;

    defaultOpenState?: boolean; // default open state of the form

    collapsable?: boolean; // whether this form can be collapsable

    url?: string; // endpoint url performs all operations
    onInitialize?(): any;
    onPrepare?(): any;
    onLoad?(data?: any, mode?: EditModes): void;
    /** Event raised after a successfull operation
    * response: the data returned from the operation
    * mode: the CRUD mode of the operation
    * closeForm: close the form automatically after success
    * -------------------------------------------------------------
    * return TRUE to fire the onLoad (load the data from the server) function
    */
    onRecordUpdated?(response?: any, mode?: EditModes, key?: string, closeForm?: boolean): boolean;
    /** event raised on error */
    onError?(error?: any, mode?: EditModes, key?: string): void,
    showCrudMessages?: boolean

    /** table to be reviewed */
    table?: string
    /** schema to be reviewed */
    schema?: string
    secondaryTable?: string,
    secondarySchema?: string,
    clearOnSave?: boolean
    clearOnDelete?: boolean,

    AddUrl?: string,
    EditUrl?: string,
    ReadUrl?: string,
    DeleteUrl?: string,
    showPanel?: boolean,
    isDirty?: boolean,
    toolbar?: any[],
    classAdd?: boolean
}

export interface ISystemFormState {
    /** specifies if form is in create mode */
    isNew: boolean;
    /** specifies if the any form field has been edited  */
    isDirty: boolean;
    /** specifies if the form is valid or not */
    isValid: boolean;
    /** specifies if the form is in a collapse state or not */
    isFormOpen: boolean;

    editMode?: EditModes; // current edit state

    /** specifies if the form is doing any background task */
    isLoading: boolean;
    /** allows the form to create */
    canCreate?: boolean;
    /** allows the form to read  */
    canRead?: boolean;
    /** allows the form to update */
    canUpdate?: boolean;
    /** allows to form to delete */
    canDelete: boolean;
    /** allows to view review */
    canReview: boolean;

    /** shows a delete confirmation before a delete operation */
    showDeleteConfirmation: boolean;

    isReadOnly?: boolean;

    node?: any

    permission: IPermission

    isAuditFormVisible: boolean
}

export default class SystemForm extends React.Component<ISystemFormProps, ISystemFormState> {

    static defaultProps = {
        id: '',
        name: '',
        url: '',
        primaryKeyEncrypted: '',
        showCrudMessages: true,
        canRead: true,
        canCreate: true,
        canUpdate: true,
        canDelete: true,
        canReview: true,
        canClear: true,
        showSave: true,
        showReview: true,
        showTitle: true,
        showClear: true,
        showTopOrBottomToolTip: true,
        ToolTipPosition: 'right'
    }

    fieldContainer: React.RefObject<HTMLDivElement> = React.createRef();
    submitButton: any = React.createRef();
    formObject: any = React.createRef();
    children: any;
    // default state of the form
    defaultPermission: IPermission = { AddPermission: true, ReadPermission: true, EditPermission: true, DeletePermission: true }

    constructor(props: ISystemFormProps) {
        super(props);
        const permission = getPermission(props.securityObject) || this.defaultPermission;

        this.state = {
            isFormOpen: true,
            editMode: EditModes.Read,
            isLoading: false,
            canRead: true,
            canCreate: true,
            canUpdate: true,
            canDelete: true,
            canReview: true,
            showDeleteConfirmation: false,
            isReadOnly: false,
            isNew: true,
            node: null,
            isValid: true,
            isDirty: false,
            isAuditFormVisible: false,
            permission: { ...permission }
        } as ISystemFormState
    }

    onProcessBegin = () => this.setState({ ...this.state, isLoading: true });
    onProcessEnd = () => this.setState({ ...this.state, isLoading: false });


    /**
     * Calls after a record has been added or edited
     * reloads the form data, return false if you want to skip loading
     * clears the form if clearonsave = true
     * @param mode : mode add or edit
     * @param response :response from the operation
     * @param closeForm
     */
    onRecordUpdated = (mode: EditModes, response: any, closeForm: boolean = false) => {
        //if (mode == EditModes.Update &&  closeForm == false && this.props.loadData) this.props.loadData(response.data); // need correction
        if (this.props.onRecordUpdated) {
            if (!this.props.onRecordUpdated(response, mode, this.props.primaryKeyEncrypted, closeForm))
                if (this.props.onLoad) this.props.onLoad(response.data, mode);
        }
        else if (this.props.onLoad) this.props.onLoad(response.data, mode);

        if (this.props.clearOnSave || (mode == EditModes.Delete && this.props.clearOnDelete)) this.handleClearButton();

        if (mode == EditModes.Delete && this.props.onAfterDelete) this.props.onAfterDelete(response);
        if (mode == EditModes.Create && this.props.onAfterCreate) this.props.onAfterCreate(response);
        if (mode == EditModes.Read && this.props.onAfterRead) this.props.onAfterRead(response);
        if (mode == EditModes.Update && this.props.onAfterUpdate) this.props.onAfterUpdate(response);
        //    this.props.onSave && this.props.onSave()
    }

    crudOptions = (): ISystemFormHelper => {
        return {
            primaryKeyEncrypted: this.props.primaryKeyEncrypted,
            showCrudMessages: this.props.showCrudMessages,
            securityObject: this.props.securityObject,
            onProcessBegin: this.onProcessBegin,
            onProcessEnd: this.onProcessEnd,
            onLoad: this.props.onLoad,
            onPrepare: this.props.onPrepare,
            onRecordUpdated: this.onRecordUpdated,
            onError: this.props.onError,
            //permission: this.state.permission
        } as ISystemFormHelper;
    }

    componentDidMount = () => {
        if (this.props.onLoad) this.props.onLoad();

        this.setState({
            node: ReactDOM.findDOMNode(this)
        }, () => this.ensureDataLoaded())
    }

    componentDidUpdate = (prevProps: ISystemFormProps) => {
        if (prevProps.primaryKeyEncrypted != this.props.primaryKeyEncrypted) {
            this.ensureDataLoaded();
        }
    }

    ensureDataLoaded = () => {
        if (this.props.primaryKeyEncrypted) {
            this.onRead();
        } else {
            this.props.onInitialize && this.props.onInitialize();
        }
    }

    /** */
    onClear = () => this.props.onInitialize && this.props.onInitialize();
    onRead = () => SystemFormHelper.onRead({ url: this.props.ReadUrl || this.props.url, ...this.crudOptions() });
    onEdit = () => SystemFormHelper.onEdit({ url: this.props.EditUrl || this.props.url, ...this.crudOptions() });
    onDelete = async () => {
        if (this.props.onDelete) {
            if (!await this.props.onDelete()) return;
        }
        SystemFormHelper.onDelete({ url: this.props.DeleteUrl || this.props.url, ...this.crudOptions() });
    }
    onAdd = () => SystemFormHelper.onAdd({ url: this.props.AddUrl || this.props.url, ...this.crudOptions() });
    onError = (error: any, mode: EditModes, primaryKey: string) => this.props.onError && this.props.onError(error, mode, primaryKey);

    handleSearchButton = () => this.props.onSearch && this.props.onSearch();

    handleClearButton = () => {
        if (this.props.onClear) this.props.onClear();
        else if (this.props.onInitialize) this.props.onInitialize();
    }

    // Event handlers
    handleCollapseButton = () => this.setState({ ...this.state, isFormOpen: !this.state.isFormOpen });
    handleReviewButton = () => this.setState({ isAuditFormVisible: true })
    handleReviewClose = () => this.setState({ isAuditFormVisible: false })
    handleDelete = () => this.setState({ ...this.state, showDeleteConfirmation: true })
    onDeleteNo = () => this.setState({ ...this.state, showDeleteConfirmation: false })
    onDeleteYes = () => this.setState({ ...this.state, showDeleteConfirmation: false }, () => this.onDelete());
    handleEdit = () => this.setState({ ...this.state, editMode: EditModes.Update });


    // handles save button click
    handleSaveAndClose = async () => {
        await this.handleSave()
        if (this.props.onSaveAndClose) this.props.onSaveAndClose()
    }

    ///** Validate the form */
    onValidate = async (): Promise<boolean> => {
        let validate: boolean = true;

        if (this.props.onValidate) validate = this.props.onValidate();
        if (validate && this.props.onValidateAsync) validate = await this.props.onValidateAsync();

        return validate;
    }

    handleSave = async (): Promise<boolean> => {
        let validate: boolean = await this.onValidate();

        if (validate) {
            if (this.props.onSave) this.props.onSave();
            else this.props.primaryKeyEncrypted ? this.onEdit() : this.onAdd();
        }

        return validate;
    }

    createNestedObject = (obj: any, path: any, value: any) => {
        var parent = obj;
        var names = path.split(".");
        for (var i = 0; i < names.length; i++) {
            if (i == names.length - 1) obj = obj[names[i]] = value
            else obj = obj[names[i]] = obj[names[i]] || {};
        }
        return parent;
    }

    createObjectFromForm = (formData: any) => {
        var obj = {};
        formData.forEach((value: any, key: any) => obj = this.createNestedObject(obj, key, value));
        return obj;
    }

    createDataFromForm = (form: HTMLFormElement) => {
        const formData = new FormData(form);
        return this.createObjectFromForm(formData);
    }
    // calls when submit button clicked
    handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        // code to do the submit
    }

    handleReload = () => this.onRead()


    // creates toolbar of the form

    render() {

        // conditions
        const isNew = !this.props.primaryKeyEncrypted;
        const defaultDisable = this.state.isReadOnly || this.state.isLoading || !this.state.permission
        const disableAdd = defaultDisable || !this.state.permission.AddPermission;
        const disableUpdate = defaultDisable || !this.state.permission.EditPermission;
        const disableDelete = defaultDisable || !this.state.permission.DeletePermission;
        const disableRead = disableAdd || disableUpdate;
        const disableSave = disableAdd || disableUpdate || this.props.disableSave || false;

        // buttons
        const TitleText = this.props.showTitle && this.props.url && this.props.label && ((isNew ? "Add " : "Edit ") + this.props.label);
        const Title = <div className="tiles-top-sec-left"><h4><SuperAdminInfo {...this.props} label={TitleText} /></h4> </div>
        const AddButton = <SystemButton disabled={disableAdd} handleClick={this.onAdd} title="Add" className="btn-default" iconName="add" btnTitle="Add" />
        const UpdateButton = <SystemButton disabled={disableUpdate} handleClick={this.handleEdit} title="Update" className="btn-default" iconName="edit" btnTitle="Update" />
        const DeleteButton = <SystemButton disabled={disableDelete} handleClick={this.handleDelete} title="Delete" className="btn-default" iconName="delete1" btnTitle="Delete" />
        const SaveButton = <SystemButton disabled={disableSave} handleClick={this.handleSave} title="Save" className="btn-default" btnType="button" iconName="save1" btnTitle="Save" />
        const SaveAndCloseButton = <SystemButton disabled={disableSave} handleClick={this.handleSaveAndClose} title="Save & Close" className="btn-default" btnType="button" iconName="save" btnTitle="Save & Exit" />
        const ValidateButton = <SystemButton handleClick={this.onValidate} title="Validate" className="btn-default" btnType="button" iconName="save" btnTitle="Validate" />
        const ReloadButton = <button onClick={this.handleReload} className="btn btn-default" title="Reload"><span className="icon-refresh"></span></button>
        const ReviewButton = <button onClick={this.handleReviewButton} className="btn btn-default" title="Review"><span className="icon-review"></span>Review</button>
        const CollapseButton = <button onClick={this.handleCollapseButton} title="Collapse Panel" className="panel-collapse-btn" >Collapse</button>
        const SearchButton = <button onClick={this.handleSearchButton} className="btn btn-default" title=""><span className="icon-search"></span>Search</button>
        const ClearButton = <button onClick={this.handleClearButton} className="btn btn-default" title="Clear"><span className="icon-refresh"></span>Clear</button>
        //<SystemDialog visible={this.state.showDeleteConfirmation} message="Are You sure you want to delete this record?" title="Confirm Delete" onYes={this.onDeleteYes} onNo={this.onDeleteNo} />

        let auditPrimaryKeyEncrypted = this.props.primaryKeyEncrypted ? this.props.primaryKeyEncrypted : this.props.primaryKeyEncrypted;
        if (this.props.primaryKeyEncrypted && this.props.primaryKeyEncrypted != undefined && this.props.primaryKeyEncrypted != null) {
            // auditPrimaryKeyEncrypted = auditPrimaryKeyEncrypted + ";" + this.props.secondaryKeyEncrypted;
            auditPrimaryKeyEncrypted = auditPrimaryKeyEncrypted
        }
        let auditSecondaryKeyEncrypted = this.props.secondaryKeyEncrypted ? this.props.secondaryKeyEncrypted : undefined;
        if (this.props.secondaryKeyEncrypted && this.props.secondaryKeyEncrypted != undefined && this.props.secondaryKeyEncrypted != null) {
            auditSecondaryKeyEncrypted = auditSecondaryKeyEncrypted
        }

        var tiles = (<div>
            {this.props.toolbar && this.props.toolbar.length > 0 && this.props.toolbar}
            {this.props.showValidate && ValidateButton}
            {this.props.showSaveAndClose && SaveAndCloseButton}
            {this.props.showSave && (this.props.canSave || this.props.canCreate || this.props.canUpdate) && SaveButton}
            {this.props.showAdd && AddButton}
            {!isNew && this.props.canDelete && DeleteButton}
            {this.props.showSearch && SearchButton}
            {isNew && this.props.canClear && ClearButton}
            {!isNew && this.props.showReview && this.props.canReview && ReviewButton}
            {!isNew && ReloadButton}

        </div>)

        let form = (
            <div key="fdiv1" className="col-md-12" >
                <AuditForm visible={this.state.isAuditFormVisible} onClose={this.handleReviewClose}
                    table={this.props.table as string} schema={this.props.schema as string}
                    primaryKeyEncrypted={auditPrimaryKeyEncrypted}
                    secondaryTable={this.props.secondaryTable as string}
                    secondarySchema={this.props.secondarySchema as string}
                    secondaryKeyEncrypted={auditSecondaryKeyEncrypted} />
                <SystemDialog visible={this.state.showDeleteConfirmation} message="Are you sure you want to delete this record?" title="Delete Record" onYes={this.onDeleteYes} onNo={this.onDeleteNo} />
                <div key="fdiv2" className="tiles tiles-grid">
                    <div key="fdiv3" className="tiles-top-sec">
                        {Title}
                        {this.props.showTopOrBottomToolTip ?
                            <div key="fdiv4" className="tiles-top-sec-right">
                                {tiles}
                            </div>
                            : ''}
                    </div>

                    <div
                        style={disableRead ? { pointerEvents: "none", opacity: "0.4" } : {}}
                        key="fdiv5" className={this.state.isFormOpen ? "tiles-btm-sec grid-open" : "tiles-btm-sec"}>
                        <div ref={this.fieldContainer} className={this.props.classAdd ? "row minheightscrollOvrride" : "row minheightscroll"} >
                            {this.props.children}
                        </div >
                        {this.props.showTopOrBottomToolTip == false ?
                            <div key="fdiv4" className="tiles-top-sec-right" style={{ float: this.props.ToolTipPosition || 'right' }}>
                                {tiles}
                            </div>
                            : ''}
                    </div>
                    {this.state.isLoading && this.state.node && <LoadingPanel node={this.state.node} />}
                </div>
            </div>
        );
        let content = this.props.showPanel ?
            <SystemPanel securityObject={this.props.securityObject} label="">
                {form}
            </SystemPanel>
            : form;
        return content;
    }
}

